import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { DocumentViewerModal } from '@/components/DocumentViewerModal';
import { Button } from '@predoc/cow';
import { ArrowLeft, History, Pencil, Download, FilePlus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { useGetPatientById } from '../../hooks/patientsAPI';
import { useGetOrganizationInfo } from '@/feature/profiles/hooks/organizationAPI';
import { PatientDemographics } from './core';
import { FacesheetAnalysis } from './analysis';
import { PatientDocumentsTabs } from './documents';
import { ExportAuditTrailDialog } from './ExportAuditTrailDialog';
import { EditPatientModal } from '../EditPatientModal';
import { DownloadDocumentsModal } from './DownloadDocumentsModal';
import { useHasPermission, Permission } from '@/utils/roles';
import { usePageTitle } from '@/hooks';

// Shared type for document data - can be moved to a types file later
export interface DocumentViewerData {
  url: string;
  title: string;
  uploadDate?: string;
  userName?: string;
  profileName?: string;
  isSplicedFile?: boolean;
}

export const PatientProfile = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  // Modal state - null means closed, DocumentViewerData means open with that document
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentViewerData | null>(null);

  const { data: patient, isLoading } = useGetPatientById(patientId);
  const { data: orgInfo } = useGetOrganizationInfo();
  const { hasPermission: canDownload } = useHasPermission(
    Permission.DOWNLOAD_DOWNLOADS,
  );
  const { hasPermission: canExportPatientAuditLogs } = useHasPermission(
    Permission.EXPORT_PATIENT_AUDIT_LOGS,
  );
  const { hasPermission: canEditPatients } = useHasPermission(
    Permission.EDIT_PATIENTS,
  );
  const { hasPermission: canRequestWorkflow } = useHasPermission(
    Permission.REQUEST_WORKFLOW,
  );
  const { hasPermission: canViewClinicalSummary } = useHasPermission(
    Permission.VIEW_CLINICAL_SUMMARY,
  );
  const patientFullName = [
    patient?.firstName,
    patient?.middleName,
    patient?.lastName,
  ]
    .filter(Boolean)
    .join(' ');
  usePageTitle('Patient');

  // hide analysis section if FSG is not enabled or user lacks permission
  const shouldShowAnalysis =
    orgInfo?.enableFacesheetRequests && canViewClinicalSummary;

  const handleGoBack = () => {
    navigate('/patients');
  };

  const handleNavToNewRequest = () => {
    navigate(`/patients/request?patientId=${patientId}`, {
      state: { fromPatientProfile: true },
    });
  };

  // Callback to open document viewer - pass this to child components
  const handleViewDocument = (document: DocumentViewerData) => {
    setSelectedDocument(document);
  };

  const handleCloseDocument = () => {
    setSelectedDocument(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader className="pl-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleGoBack}>
              <ArrowLeft className="size-4" />
            </Button>
            <div>
              <h6 className="text-muted-foreground text-sm">Patient</h6>
              <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
        </PageHeader>
        <div className="p-6">
          <div className="h-32 animate-pulse rounded-lg bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader className="pl-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleGoBack}>
              <ArrowLeft className="size-4" />
            </Button>
            <div>
              <h6 className="text-muted-foreground text-sm">Patient</h6>
              <h1 className="text-3xl font-bold">Patient not found</h1>
            </div>
          </div>
        </PageHeader>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader className="pl-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleGoBack}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h6 className="text-muted-foreground text-sm">Patient</h6>
            <h1 className="text-3xl font-bold">{patientFullName}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canExportPatientAuditLogs ? (
            <Button variant="ghost" onClick={() => setIsExportDialogOpen(true)}>
              <History className="mr-1 size-4" />
              Export Logs
            </Button>
          ) : null}
          {canEditPatients ? (
            <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
              <Pencil className="mr-1 size-4" />
              Edit Details
            </Button>
          ) : null}
          {canDownload ? (
            <Button
              variant="outline"
              onClick={() => setIsDownloadModalOpen(true)}
            >
              <Download className="mr-1 size-4" />
              Download...
            </Button>
          ) : null}
          {canRequestWorkflow ? (
            <Button onClick={handleNavToNewRequest}>
              <FilePlus className="mr-1 size-4" />
              New Request
            </Button>
          ) : null}
        </div>
      </PageHeader>

      {canExportPatientAuditLogs ? (
        <ExportAuditTrailDialog
          patientName={patientFullName}
          patientId={patient.id}
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
        />
      ) : null}

      <EditPatientModal
        patient={patient}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />

      <DownloadDocumentsModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        patientId={patient.id}
      />

      <div className="space-y-6 p-6">
        <PatientDemographics patient={patient} />
        {shouldShowAnalysis && (
          <FacesheetAnalysis
            onViewImage={(signedUrl) =>
              handleViewDocument({ url: signedUrl, title: 'Facesheet' })
            }
          />
        )}
        <PatientDocumentsTabs onViewDocument={handleViewDocument} />
      </div>

      {/* Single Document Viewer Modal - shared across all child components */}
      {selectedDocument && (
        <DocumentViewerModal
          open={!!selectedDocument}
          onClose={handleCloseDocument}
          file={selectedDocument.url}
          title={selectedDocument.title}
          uploadDate={selectedDocument.uploadDate}
          userName={selectedDocument.userName}
          profileName={selectedDocument.profileName}
          patientKey={patient.key}
          isSplicedFile={selectedDocument.isSplicedFile}
        />
      )}
    </div>
  );
};
